import { Close, Delete } from '@mui/icons-material'
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { Button, Dialog, IconButton } from '@mui/material'
import { Form, Input } from '@components'
import './modal.style.scss'

const TIME_FIELDS = ['pomodoro', 'shortBreak', 'longBreak']
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/

const dialogStyle = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  '& .MuiPaper-root': {
    width: 'clamp(360px, 46vw, 640px)',
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: 'min(720px, calc(100dvh - 48px))',
    borderRadius: '24px',
    overflow: 'hidden',
    background: '#d9d9d9',
    boxShadow: '0 24px 64px rgba(46, 127, 123, 0.2)',
  },
  '@media screen and (orientation: portrait)': {
    '& .MuiDialog-container': {
      padding: '16px',
      paddingBottom:
        'calc(var(--bottom-dock-height) + 16px + env(safe-area-inset-bottom, 0px))',
    },
    '& .MuiPaper-root': {
      width: 'min(520px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight:
        'calc(100dvh - var(--bottom-dock-height) - 42px - env(safe-area-inset-bottom, 0px))',
      borderRadius: '20px',
    },
  },
  '@media screen and (max-width: 420px) and (orientation: portrait)': {
    '& .MuiDialog-container': {
      padding: '12px',
      paddingBottom:
        'calc(var(--bottom-dock-height) + 12px + env(safe-area-inset-bottom, 0px))',
    },
    '& .MuiPaper-root': {
      width: 'calc(100vw - 24px)',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight:
        'calc(100dvh - var(--bottom-dock-height) - 32px - env(safe-area-inset-bottom, 0px))',
    },
  },
}

const timeInputProps = {
  min: 1,
  step: 1,
  inputMode: 'numeric',
  pattern: '[1-9][0-9]*',
}

const blockedTimeKeys = ['-', '+', 'e', 'E', '.', ',']

const isPositiveTime = value =>
  POSITIVE_INTEGER_PATTERN.test(String(value ?? ''))

const ModalComponent = ({
  formData,
  handleSubmit,
  handleChange,
  handleClick,
  handleClickDelete,
  handleClickClose,
  pomodoroSettingsList,
  open,
}) => {
  const canSave = TIME_FIELDS.every(field =>
    isPositiveTime(formData[field]?.value)
  )
  const visibleSettings = pomodoroSettingsList?.filter(
    ({ isVisible }) => isVisible
  )
  const modalFormData = {
    ...formData,
    pomodoro: { ...formData.pomodoro, label: 'Foco' },
    shortBreak: { ...formData.shortBreak, label: 'Curto' },
    longBreak: { ...formData.longBreak, label: 'Longo' },
  }

  const handleTimeKeyDown = event => {
    if (blockedTimeKeys.includes(event.key)) {
      event.preventDefault()
    }
  }

  return (
    <Dialog
      sx={dialogStyle}
      open={open}
      onClose={handleClickClose}
      scroll="paper"
      maxWidth={false}
    >
      <section className="pomodoro-modal">
        <header className="pomodoro-modal__header">
          <div>
            <h2 className="pomodoro-modal__title">
              Configurações
              <AccessAlarmIcon fontSize="inherit" />
            </h2>
            <p className="pomodoro-modal__subtitle">
              Ajuste seus ciclos em minutos.
            </p>
          </div>

          <IconButton
            className="pomodoro-modal__close"
            onClick={handleClickClose}
            color="secondary"
            aria-label="Fechar configurações"
          >
            <Close />
          </IconButton>
        </header>

        <Form
          onSubmit={handleSubmit}
          formData={formData}
          className="pomodoro-modal__form"
        >
          <Input
            className="pomodoro-modal__field pomodoro-modal__field--name"
            formData={formData.name}
            handleChange={handleChange}
            fullWidth
            size="small"
          />

          <div className="pomodoro-modal__time-grid">
            <Input
              className="pomodoro-modal__field"
              formData={modalFormData.pomodoro}
              handleChange={handleChange}
              fullWidth
              size="small"
              inputProps={timeInputProps}
              onKeyDown={handleTimeKeyDown}
            />
            <Input
              className="pomodoro-modal__field"
              formData={modalFormData.shortBreak}
              handleChange={handleChange}
              fullWidth
              size="small"
              inputProps={timeInputProps}
              onKeyDown={handleTimeKeyDown}
            />
            <Input
              className="pomodoro-modal__field"
              formData={modalFormData.longBreak}
              handleChange={handleChange}
              fullWidth
              size="small"
              inputProps={timeInputProps}
              onKeyDown={handleTimeKeyDown}
            />
          </div>

          <Button
            className="pomodoro-modal__save"
            variant="contained"
            color="secondary"
            type="submit"
            disabled={!canSave}
          >
            Salvar
          </Button>
        </Form>

        <section className="pomodoro-modal__saved">
          <h3 className="pomodoro-modal__saved-title">
            Pomodoros salvos
            <TaskAltIcon fontSize="inherit" />
          </h3>

          <div className="pomodoro-modal__saved-table" role="list">
            <div className="pomodoro-modal__saved-head" aria-hidden="true">
              <span>Nome</span>
              <span>Foco</span>
              <span>Curto</span>
              <span>Longo</span>
              <span />
            </div>

            {visibleSettings?.map(
              ({
                id,
                categoryName,
                focusTime,
                shortBreakTime,
                longBreakTime,
              }) => (
                <article
                  className="pomodoro-modal__saved-row"
                  key={id}
                  role="listitem"
                >
                  <button
                    type="button"
                    className="pomodoro-modal__saved-select"
                    onClick={() =>
                      handleClick({
                        id,
                        categoryName,
                        focusTime,
                        shortBreakTime,
                        longBreakTime,
                      })
                    }
                  >
                    <strong className="pomodoro-modal__saved-name">
                      {categoryName}
                    </strong>
                    <span>
                      <small>Foco</small>
                      {focusTime}
                    </span>
                    <span>
                      <small>Curto</small>
                      {shortBreakTime}
                    </span>
                    <span>
                      <small>Longo</small>
                      {longBreakTime}
                    </span>
                  </button>

                  {visibleSettings.length !== 1 ? (
                    <IconButton
                      className="pomodoro-modal__delete"
                      onClick={event =>
                        handleClickDelete({ id, evento: event })
                      }
                      aria-label={`Excluir ${categoryName}`}
                    >
                      <Delete />
                    </IconButton>
                  ) : (
                    <span className="pomodoro-modal__delete-placeholder" />
                  )}
                </article>
              )
            )}
          </div>
        </section>
      </section>
    </Dialog>
  )
}

export { ModalComponent }
